// Static catalogue of child developmental milestones, age-banded 6 weeks–5 years.
// Mirrors phm-web-backend/app/data/milestones.py. FE copy is duplicated on
// purpose so we don't ship labels/descriptions in every GET payload.
// ponytail: when this goes DB-driven, drop this file and let the GET
// response carry the catalogue.

// Band upper-bound table (months). Mirrors _BAND_UPPER_MONTHS on the BE.
// Used by any client-side sorting/filtering; the BE watchlist endpoint
// already returns ageBandUpperMonths per row so most code won't need this.
const BAND_UPPER_MONTHS = { 1: 3, 2: 6, 3: 9, 4: 12, 5: 18, 6: 24, 7: 36, 8: 48, 9: 60 };

const m = (year, ageBand, key, label, description) => ({
    year,
    ageBand,
    ageBandUpperMonths: BAND_UPPER_MONTHS[year],
    key,
    label,
    description,
});

export const MILESTONE_CATALOGUE = [
    // 6 Weeks to 3 Months
    m(1, '6 Weeks to 3 Months', 'lifts_head_stomach',
      'Lifts their head while lying on their stomach.',
      'Holds head up briefly when on tummy during supervised play.'),
    m(1, '6 Weeks to 3 Months', 'follows_moving_objects',
      'Follows moving objects with their eyes from one place to another.',
      'Tracks a toy or face across the midline without losing focus.'),
    m(1, '6 Weeks to 3 Months', 'reacts_to_loud_sounds',
      'Reacts to sudden loud noises by stopping what they are doing or changing their activity.',
      'Startles or pauses at unexpected sounds; looks toward the source.'),
    m(1, '6 Weeks to 3 Months', 'makes_simple_sounds',
      'Makes simple sounds like "Ah," "Oh," or "Ee" when someone interacts with them.',
      'Vocalises in response to a caregiver during face-to-face time.'),
    m(1, '6 Weeks to 3 Months', 'recognizes_mother',
      'Recognizes their mother and smiles at her.',
      'Shows a social smile and prefers familiar faces.'),

    // 3 to 6 Months
    m(2, '3 to 6 Months', 'lifts_head_chest',
      'Lifts their head and chest while lying on their stomach.',
      'Props up on forearms; head and chest clear the floor.'),
    m(2, '3 to 6 Months', 'plays_with_hands',
      'Plays with their hands by interlocking their fingers.',
      'Brings hands together at midline; explores fingers.'),
    m(2, '3 to 6 Months', 'reaches_for_objects',
      'Reaches for objects and grabs them using their whole hand.',
      'Sweeps toward a toy and closes the whole hand around it.'),
    m(2, '3 to 6 Months', 'turns_to_sound',
      'Turns their head in the direction of a sound.',
      'Localises a sound by turning toward it.'),
    m(2, '3 to 6 Months', 'single_syllable_sounds',
      'Makes single-syllable sounds.',
      'Produces \'ba\', \'da\', \'ga\' or similar consonant-like sounds.'),
    m(2, '3 to 6 Months', 'laughs_out_loud',
      'Laughs out loud.',
      'Shows delight with a full, audible laugh during play.'),

    // 6 to 9 Months
    m(3, '6 to 9 Months', 'lifts_head_on_back',
      'Lifts their head while lying on their back.',
      'Lifts head and shoulders to look at feet or toys above.'),
    m(3, '6 to 9 Months', 'rolls_over',
      'Rolls over from their back to their stomach and from their stomach to their back.',
      'Rolls both ways during floor play.'),
    m(3, '6 to 9 Months', 'passes_objects_between_hands',
      'Passes objects from one hand to the other.',
      'Transfers a toy smoothly without dropping it.'),
    m(3, '6 to 9 Months', 'repeats_sounds',
      'Repeats sounds like \'da-da\' or \'ta-ta\'.',
      'Strings syllables together, often non-word-like.'),

    // 9 to 12 Months
    m(4, '9 to 12 Months', 'sits_without_help',
      'Sits up without any help.',
      'Sits independently for a minute or more without support.'),
    m(4, '9 to 12 Months', 'stands_holding_furniture',
      'Stands up by holding onto furniture or objects.',
      'Pulls to stand and cruises along stable furniture.'),
    m(4, '9 to 12 Months', 'pincer_grasp',
      'Picks up small things using their thumb and index finger.',
      'Uses thumb-index pinch to pick up small items (cereal, raisin).'),
    m(4, '9 to 12 Months', 'copies_sounds',
      'Copies sounds they hear.',
      'Imitates familiar sounds or animal noises during play.'),
    m(4, '9 to 12 Months', 'says_single_words',
      'Says single words and makes sounds that have meaning.',
      'First meaningful word appears with intent (mama/dada with reference).'),
    m(4, '9 to 12 Months', 'understands_simple_instructions',
      'Understands simple instructions, like clapping hands or waving goodbye.',
      'Follows a one-step gesture or verbal cue when paired with context.'),

    // 12 to 18 Months
    m(5, '12 to 18 Months', 'walks_with_help',
      'Walks with someone helping them.',
      'Cruises or walks with one or both hands held by an adult.'),
    m(5, '12 to 18 Months', 'says_two_three_words',
      'Can say at least 2 or 3 words (for example: \'give\' or \'that\').',
      'Uses a small wordbank with intent; pronunciation varies.'),
    m(5, '12 to 18 Months', 'points_to_familiar_things',
      'Points to familiar things when asked about them.',
      'Points to a named picture, person, or object on request.'),
    m(5, '12 to 18 Months', 'rolls_small_ball',
      'Can roll a small ball.',
      'Pushes or rolls a ball forward during play.'),
    m(5, '12 to 18 Months', 'identifies_body_part',
      'Can point to or identify at least one part of the body.',
      'Points to nose, eyes, or ears on request.'),

    // 18 Months to 2 Years
    m(6, '18 Months to 2 Years', 'walks_alone',
      'Walks alone without any help.',
      'Walks steadily without support across a room.'),
    m(6, '18 Months to 2 Years', 'climbs_stairs_with_help',
      'Climbs stairs with help.',
      'Goes up stairs step-by-step with a hand held or rail support.'),
    m(6, '18 Months to 2 Years', 'builds_small_tower',
      'Builds a small tower using 2 or 3 building blocks.',
      'Stacks 2–3 blocks without knocking them over.'),
    m(6, '18 Months to 2 Years', 'feeds_themselves',
      'Feeds themselves.',
      'Uses a spoon or hands to feed with some spilling.'),
    m(6, '18 Months to 2 Years', 'says_ten_words',
      'Can say about 10 words and can speak simple sentences made of at least 2 words (like \'Dad come\').',
      'Vocabulary of ~10 words; combines two words at times.'),
    m(6, '18 Months to 2 Years', 'puckers_for_kiss',
      'Puckers their lips to give a kiss.',
      'Smushes lips forward on request or spontaneously.'),

    // 2 to 3 Years
    m(7, '2 to 3 Years', 'runs_without_falling',
      'Runs without falling over.',
      'Runs across a room with reasonably steady balance.'),
    m(7, '2 to 3 Years', 'walks_stairs_without_falling',
      'Walks up and down the stairs without falling.',
      'Alternates feet on steps with light rail support.'),
    m(7, '2 to 3 Years', 'copies_circle',
      'Can draw or copy a circle shape.',
      'Draws a closed, roughly round shape on paper.'),
    m(7, '2 to 3 Years', 'three_word_sentences',
      'Can put together sentences using 3 or more words.',
      'Speaks in short, mostly understandable sentences.'),

    // 3 to 4 Years
    m(8, '3 to 4 Years', 'stands_on_one_leg',
      'Stands on one leg.',
      'Holds one-foot balance for ~2 seconds with arms out.'),
    m(8, '3 to 4 Years', 'jumps_from_step',
      'Can jump down from a step.',
      'Jumps down from a low step landing with both feet.'),
    m(8, '3 to 4 Years', 'puts_on_shoes_clothes',
      'Puts on their own shoes and clothes (but cannot do buttons yet).',
      'Dresses with help for buttons, snaps, or zippers.'),
    m(8, '3 to 4 Years', 'copies_circles_and_patterns',
      'Copies circles and simple patterns.',
      'Copies a circle, plus, or other simple shape after seeing it.'),
    m(8, '3 to 4 Years', 'counts_to_three',
      'Can count up to three.',
      'Recites or points to three objects in order.'),
    m(8, '3 to 4 Years', 'understands_position_words',
      'Understands position words, knowing at least two concepts like up/down or near/far.',
      'Follows position words in simple instructions.'),
    m(8, '3 to 4 Years', 'speaks_complete_sentences',
      'Speaks in complete sentences and can use complex sentences.',
      'Speaks in full sentences understood by strangers most of the time.'),

    // 4 to 5 Years
    m(9, '4 to 5 Years', 'hops_on_one_leg',
      'Hops on one leg.',
      'Hops forward several times on one foot.'),
    m(9, '4 to 5 Years', 'dresses_completely',
      'Dresses themselves completely.',
      'Puts on all clothing independently; manages buttons or fastenings.'),
    m(9, '4 to 5 Years', 'eats_meals_alone',
      'Eats their meals completely by themselves.',
      'Uses utensils and finishes a meal with minimal spillage.'),
    m(9, '4 to 5 Years', 'draws_person',
      'Can draw a simple picture of a person.',
      'Draws a recognisable person with head, body, and limbs.'),
    m(9, '4 to 5 Years', 'plays_with_older_children',
      'Plays and cooperates well with older children.',
      'Engages in cooperative play with peers of various ages.'),
    m(9, '4 to 5 Years', 'describes_picture_tenses',
      'Can describe a picture using the correct past, present, and future action words.',
      'Uses past/present/future tenses to narrate a picture scene.'),
    m(9, '4 to 5 Years', 'says_name_and_age',
      'Can say their full name and their age.',
      'States full name and age correctly when asked.'),
];

// YES/NO selection values. Matches the BE `MilestoneEntryOut.selection` enum
// and the persisted `child_milestones.selection` column.
export const SELECTIONS = ['yes', 'no'];
export const SELECTION_LABELS = {
    yes: 'YES',
    no: 'NO',
};

// Row-tint hint per selection — yes rows tint success-tinted, no rows
// warning-tinted, null leaves the default surface.
export const ROW_SEVERITY = {
    yes: 'success',
    no: 'warning',
};
